import { Dispatch, SetStateAction, useMemo, type JSX } from 'react';
import { defineMessages } from 'react-intl';
import dynamic from 'next/dynamic';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import SharedPopover from '@alltrails/shared/components/Popover';
import Overflow from '@alltrails/denali/icons/Overflow';
import IconButtonWithTooltip from '@alltrails/denali/components/IconButtonWithTooltip';
import LinkButtonOrDiv from '@alltrails/denali/components/LinkButtonOrDiv';
import { UserConnectionActionType } from '@alltrails/shared/types/belay';
import SuccessToast from '@alltrails/shared/types/successToast';
import styles from './styles/styles.module.scss';

const UserConnectionOptions = dynamic(() => import('./UserConnectionOptions'), { ssr: false });

const messages = defineMessages({
  REPORT_AN_ISSUE: { defaultMessage: 'Report an issue' },
  MORE_OPTIONS: { defaultMessage: 'More options' },
  RESPOND: { defaultMessage: 'Respond' },
  DELETE: { defaultMessage: 'Delete' },
  EDIT: { defaultMessage: 'Edit' },
  ADMIN: { defaultMessage: 'ADMIN' },
  EXCLUDE_FROM_SUMMARY: { defaultMessage: 'Exclude from summary' },
  ADD_TO_SUMMARY: { defaultMessage: 'Add to summary' }
});

export type TrailReviewOverflowMenuProps = {
  isThirdPartyReview: boolean;
  isMoreOptionsOpen: boolean;
  setIsMoreOptionsOpen: Dispatch<SetStateAction<boolean>>;
  onConnectClick?: (actionType: UserConnectionActionType) => void;
  setIsReportingModalOpen: (isOpen: boolean) => void;
  canComment?: boolean;
  isEditable?: boolean;
  isAdmin?: boolean;
  hideReplies?: boolean;
  onCommentClick?: () => void;
  onDeleteClick?: () => void;
  onEditClick?: () => void;
  setReportingSuccessToast?: Dispatch<SetStateAction<SuccessToast>>;
  summaryExclusion?: boolean;
  onToggleSummaryExclusion?: (summaryExclusion: boolean) => void;
  reviewUserId: number;
};

const TrailReviewOverflowMenu = ({
  isThirdPartyReview,
  isMoreOptionsOpen,
  setIsMoreOptionsOpen,
  onConnectClick,
  setIsReportingModalOpen,
  canComment,
  isEditable,
  isAdmin,
  hideReplies,
  onCommentClick,
  onDeleteClick,
  onEditClick,
  setReportingSuccessToast,
  summaryExclusion,
  onToggleSummaryExclusion,
  reviewUserId
}: TrailReviewOverflowMenuProps): JSX.Element => {
  const {
    formattedDefaultMessages: { MORE_OPTIONS, RESPOND, DELETE, EDIT, ADMIN, EXCLUDE_FROM_SUMMARY, ADD_TO_SUMMARY }
  } = useFormatMessage(messages);

  const onClickRemoveFromSummary = useMemo(() => {
    if (onToggleSummaryExclusion) {
      return () => {
        onToggleSummaryExclusion?.(true);
        setIsMoreOptionsOpen(false);
      };
    }
    return undefined;
  }, [onToggleSummaryExclusion, setIsMoreOptionsOpen]);

  const onClickAddToSummary = useMemo(() => {
    if (onToggleSummaryExclusion) {
      return () => {
        onToggleSummaryExclusion?.(false);
        setIsMoreOptionsOpen(false);
      };
    }
    return undefined;
  }, [onToggleSummaryExclusion, setIsMoreOptionsOpen]);

  const handleEditClick = useMemo(() => {
    if (onEditClick) {
      return () => {
        onEditClick?.();
        setIsMoreOptionsOpen(false);
      };
    }
    return undefined;
  }, [onEditClick, setIsMoreOptionsOpen]);

  return (
    <SharedPopover
      alignment="bottom-right"
      isOpen={isMoreOptionsOpen}
      popperClassName={styles.popover}
      ignoreContentClick
      verticalOffset={8}
      horizontalOffset={0}
      onCloseRequest={() => setIsMoreOptionsOpen(false)}
      anchorElement={
        <div>
          <IconButtonWithTooltip
            icon={{ Component: Overflow }}
            onClick={() => setIsMoreOptionsOpen(!isMoreOptionsOpen)}
            testId="more-options"
            title={MORE_OPTIONS}
            variant="flat"
            size="sm"
          />
        </div>
      }
    >
      <div className={styles.menuList}>
        {isThirdPartyReview && isMoreOptionsOpen && (
          <UserConnectionOptions
            reviewUserId={reviewUserId}
            setIsReportingModalOpen={setIsReportingModalOpen}
            setReportingSuccessToast={setReportingSuccessToast}
            onConnectClick={onConnectClick}
          />
        )}
        {isEditable && (
          <div className={styles.adminOptions}>
            <div className={styles.divider} />
            {isAdmin && <div className={styles.adminItem}>{ADMIN}</div>}
            {onDeleteClick && (
              <LinkButtonOrDiv className={styles.menuItem} testId="deleteReview" onClick={onDeleteClick} key={DELETE}>
                <span> {DELETE} </span>
              </LinkButtonOrDiv>
            )}
            {!hideReplies && canComment && onCommentClick ? (
              <LinkButtonOrDiv className={styles.menuItem} testId="commentReview" onClick={onCommentClick} key={RESPOND}>
                <span> {RESPOND} </span>
              </LinkButtonOrDiv>
            ) : (
              !isThirdPartyReview &&
              handleEditClick && (
                <LinkButtonOrDiv className={styles.menuItem} testId="editReview" onClick={handleEditClick} key={EDIT}>
                  <span> {EDIT} </span>
                </LinkButtonOrDiv>
              )
            )}
            {isAdmin && !summaryExclusion && onClickRemoveFromSummary && (
              <LinkButtonOrDiv
                className={styles.menuItem}
                testId="excludeReviewFromSummary"
                onClick={onClickRemoveFromSummary}
                key="Exclude from summary"
              >
                <span> {EXCLUDE_FROM_SUMMARY} </span>
              </LinkButtonOrDiv>
            )}
            {isAdmin && summaryExclusion && onClickAddToSummary && (
              <LinkButtonOrDiv className={styles.menuItem} testId="addReviewToSummary" onClick={onClickAddToSummary} key="Add to summary">
                <span> {ADD_TO_SUMMARY} </span>
              </LinkButtonOrDiv>
            )}
          </div>
        )}
      </div>
    </SharedPopover>
  );
};

export default TrailReviewOverflowMenu;
